import { useEffect, useState } from "react";
import Account from "byzantine/src/Account";
import {
  groupAccounts,
  sortAccountGroups,
  DEFAULT_SORT,
} from "byzantine/src/AccountUtils";
import ApiHttp from "byzantine/src/ApiHttp";

/**
 * Converts a product group name to the server-compatible format.
 * Maps product group names (from Account.PRODUCT_GROUPS) to their server equivalents.
 *
 * @param {string} groupName - The product group name (e.g., "Credit cards", "Checking")
 * @returns {string} The server-compatible group name (e.g., "credit_cards", "checking")
 */
export const convertToServerGroupName = (groupName) => {
  const groupNameMap = {
    Favorites: "favorites",
    Checking: "checking",
    Savings: "savings",
    "Credit cards": "credit_cards",
    Loans: "loans",
    CDs: "cds",
    Others: "others",
    "External accounts": "external_accounts",
  };
  return groupNameMap[groupName] || groupName.toLowerCase().replace(/ /g, "_");
};

const useAccountSorting = (accounts) => {
  const [accountSorting, setAccountSorting] = useState([]);
  const { order: DEFAULT_ORDER, sort_by: DEFAULT_SORT_BY } = DEFAULT_SORT;

  const getAccountSortingForGroup = (groupName) =>
    accountSorting.find(
      (acctSort) => acctSort.group_name === convertToServerGroupName(groupName)
    ) || {};

  const fetchAccountSorting = () => {
    ApiHttp.fetch("accounts/sorting", { method: "GET" })
      .then((response) => {
        const { account_sorting } = response;
        const groupsWithSort = account_sorting.map((sort) => sort.group_name);

        Object.keys(Account.PRODUCT_GROUPS).forEach((groupName) => {
          const serverGroupName = convertToServerGroupName(groupName);
          if (!groupsWithSort.includes(serverGroupName)) {
            account_sorting.push({
              group_name: serverGroupName,
              sort_by: DEFAULT_SORT_BY,
              order: DEFAULT_ORDER,
            });
          }
        });

        setAccountSorting(account_sorting);
      })
      .catch(() => {});
  };
  useEffect(fetchAccountSorting, []);

  const sortGroup = (groupName, sort_by, order) => ApiHttp.fetch(
      "accounts/sorting",
      { method: "POST" },
      {
        group_name: convertToServerGroupName(groupName),
        sort_by: sort_by || DEFAULT_SORT_BY,
        order: order || DEFAULT_ORDER,
      }
    ).then(({ account_sorting }) => {
      setAccountSorting((prevAccountSorting) => {
        const updatedAccountSorting = [...prevAccountSorting];
        const prevIndex = updatedAccountSorting.findIndex(
          (accSort) => accSort.group_name === account_sorting.group_name
        );
        if (prevIndex > -1) updatedAccountSorting[prevIndex] = account_sorting;
        else updatedAccountSorting.push(account_sorting);
        return updatedAccountSorting;
      });
    });

  const groupedAccounts = groupAccounts(accounts);
  sortAccountGroups(groupedAccounts, accountSorting);
  const orderedAccounts = Array.from(groupedAccounts.values()).flat();

  return {
    orderedAccounts,
    sortGroup,
    getAccountSortingForGroup,
  };
};

export default useAccountSorting;
